import React, { useCallback, useContext, useEffect, useState } from "react";

import { Modal } from "@magnetic/modal";
import { Notification } from "@magnetic/notification";

import PageHeader from "src/common/pageHeader";
import { Input } from "src/components";
import { Button } from "src/components/buttons";
import { ErrorNotification } from "src/components/errorNotification";
import { HFlex } from "src/components/hFlex";
import { VFlex } from "src/components/vFlex";
import { ApiError } from "src/core/apiError";
import { LoginContext } from "src/core/loginContext";
import { Fred } from "src/gen/rest/fred/FredService";
import { AddOrgRequest, AddOrgResponse } from "src/gen/schema/brig/bapi";
import { useForm } from "src/hooks";
import { dispatchC3TenantSwitchEvent } from "src/utils/c3Utils";
import { submitAddOrgViaFormPost } from "src/utils/crossGeoFormPost";
import { shouldUseUnspecifiedOrgGeo } from "src/utils/region/localGeo";
import { RegionSelect } from "src/utils/region/RegionSelect";
import {
  GeographicRegion,
  geographicRegionToGeo
} from "src/utils/region/regionTypes";
import {
  geographicRegionFromHostname,
  geographicRegionFromSessionContext
} from "src/utils/region/sessionGeo";

type OrgFormFields = {
  name: string;
  region: GeographicRegion | undefined;
};

const defaultFormValues: OrgFormFields = {
  name: "",
  region: undefined
};

interface OrgModalProps {
  readonly onClose: () => void;
}

export const OrgModal = ({ onClose }: OrgModalProps): React.ReactNode => {
  const { session, userContexts, switchTenant } = useContext(LoginContext);
  const { form, handleInputChange, handleFormValueChange, setForm } =
    useForm(defaultFormValues);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | undefined>();
  const useUnspecifiedOrgGeo = shouldUseUnspecifiedOrgGeo();

  useEffect(() => {
    if (useUnspecifiedOrgGeo) {
      return;
    }
    const r = geographicRegionFromSessionContext(session, userContexts);
    if (r) {
      setForm((prev) =>
        prev.region === undefined ? { ...prev, region: r } : prev
      );
    }
  }, [session, userContexts, setForm, useUnspecifiedOrgGeo]);

  const handleRegionChange = useCallback(
    (region: GeographicRegion | undefined) => {
      handleFormValueChange({ ...form, region });
    },
    [form, handleFormValueChange]
  );

  const onSubmit = useCallback(() => {
    setError(undefined);
    const trimmed = form.name.trim();
    if (!trimmed) {
      setError(ApiError.fromMessage("Organization name is required."));
      return;
    }
    if (!useUnspecifiedOrgGeo && !form.region) {
      setError(ApiError.fromMessage("Select a geographic region."));
      return;
    }

    const submittedRegion = useUnspecifiedOrgGeo ? undefined : form.region;
    const targetGeo = geographicRegionToGeo(submittedRegion);
    const currentRegion =
      geographicRegionFromSessionContext(session, userContexts) ??
      geographicRegionFromHostname();
    const isCrossGeo =
      submittedRegion !== undefined &&
      currentRegion !== undefined &&
      submittedRegion !== currentRegion;

    setLoading(true);

    if (isCrossGeo) {
      submitAddOrgViaFormPost(trimmed, targetGeo);
      return;
    }
    Fred.addOrg(AddOrgRequest.create({ orgName: trimmed, targetGeo }))
      .then((resp: AddOrgResponse) => {
        if (resp) {
          if (session?.isC3Crosslaunch) {
            dispatchC3TenantSwitchEvent(resp.cuiTenantId);
            return;
          }
          switchTenant(resp.orgId);
        }
      })
      .catch((err: ApiError) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    form.name,
    form.region,
    session,
    userContexts,
    switchTenant,
    useUnspecifiedOrgGeo
  ]);

  return (
    <Modal isOpen={true} size="md" data-qa="org-modal" className="org-modal">
      <VFlex gap="sm">
        <PageHeader title={"Create new organization"} />
        <Notification status="informational">
          Once an organization is created it cannot be deleted.
        </Notification>
        <ErrorNotification error={error} />
        <VFlex gap="sm">
          <Input
            key="org-name"
            name="name"
            id="org-name"
            value={form.name}
            label="Organization name"
            required={true}
            size="md"
            autoComplete="off"
            onChange={handleInputChange}
          />
          <RegionSelect
            value={form.region}
            onChange={handleRegionChange}
            disabled={useUnspecifiedOrgGeo}
            includeUnspecified={useUnspecifiedOrgGeo}
            required={!useUnspecifiedOrgGeo}
          />
        </VFlex>
        <HFlex justify="flex-end">
          <Button.Group>
            <Button
              name="org-cancel"
              kind="tertiary"
              size="md"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              name="org-save"
              onClick={onSubmit}
              size="md"
              loading={loading}
              disabled={
                loading ||
                !form.name?.trim() ||
                (!useUnspecifiedOrgGeo && !form.region)
              }
            >
              Save
            </Button>
          </Button.Group>
        </HFlex>
      </VFlex>
    </Modal>
  );
};
